import React, { useState, useEffect } from 'react';
import { formsAPI } from '../../services/apiService';
import { Mail, Clock, Eye, Trash2, Filter, Check, X } from 'lucide-react';

const AdminForms = () => {
  const [contacts, setContacts] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('contacts');
  const [filterRead, setFilterRead] = useState('all');
  const [selectedItem, setSelectedItem] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    fetchData();
  }, [activeTab, filterRead]);

  const fetchData = async () => {
    try {
      if (activeTab === 'contacts') {
        const params = filterRead !== 'all' ? { read: filterRead === 'read' } : {};
        const response = await formsAPI.getContacts(params);
        setContacts(response.data);
      } else {
        const params = filterRead !== 'all' ? { read: filterRead === 'read' } : {};
        const response = await formsAPI.getBookings(params);
        setBookings(response.data);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleMarkAsRead = async (id, type) => {
    try {
      if (type === 'contact') {
        await formsAPI.updateContact(id, { read: true });
      } else {
        await formsAPI.updateBooking(id, { read: true });
      }
      await fetchData();
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  const handleDelete = async (id, type) => {
    if (window.confirm('Вы уверены, что хотите удалить этот элемент?')) {
      try {
        if (type === 'contact') {
          await formsAPI.deleteContact(id);
        } else {
          await formsAPI.deleteBooking(id);
        }
        await fetchData();
      } catch (error) {
        console.error('Error deleting item:', error);
        alert('Error deleting item');
      }
    }
  };

  const handleUpdateBookingStatus = async (id, status) => {
    try {
      await formsAPI.updateBooking(id, { status });
      await fetchData();
      setShowModal(false);
    } catch (error) {
      console.error('Error updating booking status:', error);
    }
  };

  const handleViewDetails = (item) => {
    setSelectedItem(item);
    setShowModal(true);
  };

  const currentData = activeTab === 'contacts' ? contacts : bookings;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-gray-300"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-white">Управление формами</h1>
        <p className="text-gray-300">Управление контактными формами и заявками на бронирование</p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-700">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('contacts')}
            className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 transition-colors duration-200 ${
              activeTab === 'contacts'
                ? 'border-indigo-500 text-indigo-400'
                : 'border-transparent text-gray-400 hover:text-gray-200 hover:border-gray-600'
            }`}
          >
            <Mail className="h-5 w-5" />
            <span>Контактные формы ({contacts.length})</span>
          </button>
          <button
            onClick={() => setActiveTab('bookings')}
            className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 transition-colors duration-200 ${
              activeTab === 'bookings'
                ? 'border-indigo-500 text-indigo-400'
                : 'border-transparent text-gray-400 hover:text-gray-200 hover:border-gray-600'
            }`}
          >
            <Clock className="h-5 w-5" />
            <span>Заявки на бронирование ({bookings.length})</span>
          </button>
        </nav>
      </div>

      {/* Filters */}
      <div className="bg-gray-800 p-4 rounded-lg shadow-lg border border-gray-700">
        <div className="flex items-center space-x-4">
          <Filter className="h-5 w-5 text-gray-400" />
          <select
            value={filterRead}
            onChange={(e) => setFilterRead(e.target.value)}
            className="border border-gray-600 bg-gray-700 text-white rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <option value="all">Все элементы</option>
            <option value="unread">Только непрочитанные</option>
            <option value="read">Только прочитанные</option>
          </select>
          <div className="text-sm text-gray-300">
            {currentData.length} {currentData.length === 1 ? 'элемент' : currentData.length < 5 ? 'элемента' : 'элементов'}
          </div>
        </div>
      </div>

      {/* Data List */}
      <div className="bg-gray-800 shadow-lg overflow-hidden sm:rounded-md border border-gray-700">
        <ul className="divide-y divide-gray-700">
          {currentData.map((item) => (
            <li key={item.id}>
              <div className="px-4 py-4 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-2 gap-2">
                    <div className="flex items-center space-x-3 min-w-0 flex-1">
                      <p className="text-lg font-medium text-white truncate">
                        {item.name}
                      </p>
                      {!item.read && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-900 text-red-200 border border-red-700 flex-shrink-0">
                          New
                        </span>
                      )}
                      {activeTab === 'bookings' && (
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium flex-shrink-0 ${
                          item.status === 'pending' 
                            ? 'bg-yellow-900 text-yellow-200 border border-yellow-700'
                            : item.status === 'confirmed'
                            ? 'bg-green-900 text-green-200 border border-green-700'
                            : 'bg-red-900 text-red-200 border border-red-700'
                        }`}>
                          {item.status}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-400 flex-shrink-0">
                      {new Date(item.created_at).toLocaleDateString()} {new Date(item.created_at).toLocaleTimeString()}
                    </p>
                  </div>
                  <p className="text-sm text-gray-300 truncate">
                    {item.email}
                  </p>
                  {activeTab === 'bookings' && (
                    <p className="text-sm text-gray-400 mt-1 truncate">
                      {item.service} • {item.date}
                    </p>
                  )}
                  <p className="text-sm text-gray-400 mt-2 line-clamp-2 sm:truncate">
                    {item.message || 'No message'}
                  </p>
                </div>
                <div className="flex items-center justify-end space-x-2 flex-shrink-0">
                  <button
                    onClick={() => handleViewDetails(item)}
                    className="p-2 text-gray-400 hover:text-indigo-400 transition-colors duration-200 bg-gray-700 hover:bg-gray-600 rounded-md"
                    title="View Details"
                  >
                    <Eye className="h-4 w-4" />
                  </button>
                  {!item.read && (
                    <button
                      onClick={() => handleMarkAsRead(item.id, activeTab === 'contacts' ? 'contact' : 'booking')}
                      className="p-2 text-gray-400 hover:text-green-400 transition-colors duration-200 bg-gray-700 hover:bg-gray-600 rounded-md"
                      title="Отметить как прочитанное"
                    >
                      <Check className="h-4 w-4" />
                    </button>
                  )}
                  <button
                    onClick={() => handleDelete(item.id, activeTab === 'contacts' ? 'contact' : 'booking')}
                    className="p-2 text-gray-400 hover:text-red-400 transition-colors duration-200 bg-gray-700 hover:bg-gray-600 rounded-md"
                    title="Удалить"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>

      {currentData.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-300 text-lg">
            No {activeTab === 'contacts' ? 'contact submissions' : 'booking requests'} found
          </p>
          <p className="text-gray-400 text-sm mt-2">
            Forms will appear here when visitors submit them through the website
          </p>
        </div>
      )}

      {/* Details Modal */}
      {showModal && selectedItem && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-80 flex items-center justify-center p-4">
          <div className="bg-gray-800 border border-gray-700 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-white">
                  {activeTab === 'contacts' ? 'Contact Details' : 'Booking Details'}
                </h2>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-gray-400 hover:text-white transition-colors duration-200"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300">Name</label>
                  <p className="mt-1 text-sm text-white">{selectedItem.name}</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300">Email</label>
                  <p className="mt-1 text-sm text-white">{selectedItem.email}</p>
                </div>
                
                {activeTab === 'bookings' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-300">Phone</label>
                      <p className="mt-1 text-sm text-white">{selectedItem.phone}</p>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-300">Service</label>
                      <p className="mt-1 text-sm text-white capitalize">{selectedItem.service}</p>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-300">Preferred Date</label>
                      <p className="mt-1 text-sm text-white">{selectedItem.date}</p>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-300">Status</label>
                      <div className="mt-1 flex items-center space-x-2">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          selectedItem.status === 'pending' 
                            ? 'bg-yellow-900 text-yellow-200 border border-yellow-700'
                            : selectedItem.status === 'confirmed'
                            ? 'bg-green-900 text-green-200 border border-green-700'
                            : 'bg-red-900 text-red-200 border border-red-700'
                        }`}>
                          {selectedItem.status}
                        </span>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleUpdateBookingStatus(selectedItem.id, 'confirmed')}
                            className="text-xs px-2 py-1 bg-green-600 text-white rounded hover:bg-green-700 transition-colors duration-200"
                          >
                            Confirm
                          </button>
                          <button
                            onClick={() => handleUpdateBookingStatus(selectedItem.id, 'cancelled')}
                            className="text-xs px-2 py-1 bg-red-600 text-white rounded hover:bg-red-700 transition-colors duration-200"
                          >
                            Отменить
                          </button>
                        </div>
                      </div>
                    </div>
                  </>
                )}
                
                <div>
                  <label className="block text-sm font-medium text-gray-300">Сообщение</label>
                  <p className="mt-1 text-sm text-white whitespace-pre-wrap bg-gray-700 p-3 rounded border border-gray-600">
                    {selectedItem.message || 'Сообщение не предоставлено'}
                  </p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300">Отправлено</label>
                  <p className="mt-1 text-sm text-white">
                    {new Date(selectedItem.created_at).toLocaleString()}
                  </p>
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 mt-6 pt-6 border-t border-gray-700">
                <button
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 border border-gray-600 text-gray-300 bg-gray-700 rounded-md hover:bg-gray-600 transition-colors duration-200"
                >
                  Close
                </button>
                {!selectedItem.read && (
                  <button
                    onClick={() => {
                      handleMarkAsRead(selectedItem.id, activeTab === 'contacts' ? 'contact' : 'booking');
                      setShowModal(false);
                    }}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors duration-200"
                  >
                    Отметить как прочитанное
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminForms;